import React, { useState, useMemo } from 'react';
import type { FlattenedSession } from '../../hooks/useAdminData';
import type { PlayedGameEntry } from '../../types';
import { formatDuration } from '../../utils/formatTime';
import {
  getTodayLocalDate,
  parseLocalDate,
  addDays,
  getDayBoundaries,
  formatDisplayDate,
} from '../../utils/dateUtils';
import { Button } from '../shared/Button';

interface AgendaViewProps {
  sessions: FlattenedSession[];
}

export function AgendaView({ sessions }: AgendaViewProps) {
  // Selected date as "YYYY-MM-DD"
  const [selectedDateStr, setSelectedDateStr] = useState<string>(() => {
    return getTodayLocalDate();
  });

  const [viewMode, setViewMode] = useState<'timeline' | 'list'>('timeline');

  // Navigate date
  const changeDay = (deltaDays: number) => {
    setSelectedDateStr((prev) => addDays(prev, deltaDays));
  };

  const goToToday = () => {
    setSelectedDateStr(getTodayLocalDate());
  };

  // Filter sessions that took place on the selected day
  const daySessions = useMemo(() => {
    const { startOfDay, endOfDay } = getDayBoundaries(selectedDateStr);

    return sessions
      .filter((s) => {
        if (!s.startedAt && !s.endedAt) return false;
        const start = s.startedAt || s.endedAt;
        const end = s.endedAt || s.startedAt;
        return (
          (start >= startOfDay && start <= endOfDay) ||
          (end >= startOfDay && end <= endOfDay)
        );
      })
      .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  }, [sessions, selectedDateStr]);

  // Daily stats
  const totalDurationDay = daySessions.reduce((acc, s) => acc + (s.totalDurationMs || 0), 0);
  const uniqueRooms = new Set(daySessions.map((s) => s.roomName || 'Salle sans nom')).size;
  const uniqueUsers = new Set(daySessions.map((s) => s.stoppedBy || 'Anonyme')).size;

  // Formatted date label
  const formattedDayLabel = useMemo(() => {
    return formatDisplayDate(selectedDateStr);
  }, [selectedDateStr]);

  // Export day to iCalendar (.ics) format
  const exportDayICS = () => {
    if (daySessions.length === 0) return;

    const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
    const toICSDate = (ts: number) => {
      const d = new Date(ts);
      return (
        d.getUTCFullYear() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        'T' +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) +
        'Z'
      );
    };

    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Frame Perfect//Namm Session Timer//FR',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
    ].join('\r\n');

    daySessions.forEach((s) => {
      const start = toICSDate(s.startedAt || s.endedAt || Date.now());
      const end = toICSDate(s.endedAt || s.startedAt || Date.now());
      const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
        ? s.playedGames
        : s.playedGames
        ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
        : [];
      const gamesDesc = games.length > 0 ? `\\nJeux: ${games.map((g) => g.game).join(', ')}` : '';

      icsContent += '\r\n' + [
        'BEGIN:VEVENT',
        `UID:${s.id}@namm.frame-perfect.fr`,
        `DTSTAMP:${toICSDate(Date.now())}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:[${s.roomName || 'Salle'}] ${s.timerName || 'Session'}`,
        `DESCRIPTION:Session chronométrée par ${s.stoppedBy || 'Anonyme'}\\nDurée: ${formatDuration(s.totalDurationMs || 0)}\\nSalle: ${s.roomName || 'Salle'}${gamesDesc}`,
        `STATUS:CONFIRMED`,
        'END:VEVENT',
      ].join('\r\n');
    });

    icsContent += '\r\nEND:VCALENDAR';

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `namm-agenda-${selectedDateStr}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Generate 24 hours markers (0h to 23h)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="space-y-6">
      {/* Date Navigation & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border border-namm-line/30 bg-namm-panel/70">
        {/* Navigation Buttons */}
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => changeDay(-1)}>
            ◀ Précédent
          </Button>
          <Button variant="primary" size="sm" onClick={goToToday}>
            Aujourd'hui
          </Button>
          <Button variant="secondary" size="sm" onClick={() => changeDay(1)}>
            Suivant ▶
          </Button>
          <input
            type="date"
            value={selectedDateStr}
            onChange={(e) => e.target.value && setSelectedDateStr(e.target.value)}
            className="bg-namm-panel-soft border border-namm-line/60 px-3 py-1.5 text-xs text-namm-cyan outline-none focus:border-namm-cyan"
          />
        </div>

        {/* Selected Date Header */}
        <div className="text-center md:text-right">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-namm-cyan capitalize">
            {formattedDayLabel}
          </h2>
          <div className="text-xs text-namm-muted mt-0.5">
            {daySessions.length} session{daySessions.length > 1 ? 's' : ''} ·{' '}
            <span className="text-green-400 font-semibold">{formatDuration(totalDurationDay)}</span> ·{' '}
            {uniqueRooms} salle{uniqueRooms > 1 ? 's' : ''} · {uniqueUsers} participant{uniqueUsers > 1 ? 's' : ''}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <div className="flex border border-namm-line/40">
            <button
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                viewMode === 'timeline'
                  ? 'bg-namm-cyan/20 text-namm-cyan font-semibold'
                  : 'text-namm-muted hover:text-namm-text'
              }`}
            >
              📅 Planning
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                viewMode === 'list'
                  ? 'bg-namm-cyan/20 text-namm-cyan font-semibold'
                  : 'text-namm-muted hover:text-namm-text'
              }`}
            >
              📋 Liste
            </button>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={exportDayICS}
            disabled={daySessions.length === 0}
            title="Exporter pour Google Calendar / Outlook / Apple Calendar"
          >
            🗓️ Exporter .ICS
          </Button>
        </div>
      </div>

      {/* Empty State */}
      {daySessions.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-namm-line/30 bg-namm-panel/30">
          <div className="text-4xl mb-3">📅</div>
          <p className="text-sm font-heading uppercase tracking-wide text-namm-text">
            Aucune session enregistrée ce jour-là
          </p>
          <p className="text-xs text-namm-muted mt-1 max-w-sm mx-auto">
            Utilise les flèches ou le calendrier ci-dessus pour naviguer vers un autre jour.
          </p>
        </div>
      ) : viewMode === 'timeline' ? (
        /* Visual Timeline Schedule (Vue Emploi du Temps) */
        <div className="border border-namm-line/30 bg-namm-panel/60 p-4 overflow-x-auto">
          <div className="min-w-[700px] space-y-1">
            {hours.map((hour) => {
              const baseDate = parseLocalDate(selectedDateStr);
              const hourStart = new Date(
                baseDate.getFullYear(),
                baseDate.getMonth(),
                baseDate.getDate(),
                hour,
                0,
                0,
                0
              ).getTime();
              const hourEnd = hourStart + 3600000;

              // Sessions that overlap this hour
              const matchingSessions = daySessions.filter((s) => {
                const start = s.startedAt || s.endedAt || 0;
                const end = s.endedAt || s.startedAt || 0;
                return start < hourEnd && end >= hourStart;
              });

              return (
                <div
                  key={hour}
                  className={`flex items-stretch border-b border-namm-line/15 min-h-[46px] ${
                    matchingSessions.length > 0 ? 'bg-namm-panel-soft/30' : ''
                  }`}
                >
                  {/* Hour label */}
                  <div className="w-16 py-2 pr-3 text-right text-xs text-namm-muted shrink-0 border-r border-namm-line/25 font-mono">
                    {hour.toString().padStart(2, '0')}:00
                  </div>

                  {/* Sessions column */}
                  <div className="flex-1 p-1.5 flex flex-wrap gap-2 items-center">
                    {matchingSessions.map((s) => {
                      const startTime = s.startedAt
                        ? new Date(s.startedAt).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-';
                      const endTime = s.endedAt
                        ? new Date(s.endedAt).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-';

                      const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
                        ? s.playedGames
                        : s.playedGames
                        ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
                        : [];

                      return (
                        <div
                          key={s.id}
                          className="px-3 py-1.5 border border-namm-cyan/40 bg-gradient-to-r from-namm-cyan/10 to-namm-violet/10 rounded flex items-center gap-3 shadow-[0_0_10px_rgba(0,229,255,0.06)] hover:border-namm-cyan transition-all"
                        >
                          <div className="w-2 h-2 rounded-full bg-namm-cyan shrink-0 animate-pulse" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-heading font-bold text-namm-text uppercase">
                                {s.timerName || 'Session'}
                              </span>
                              <span className="text-[10px] text-namm-cyan font-mono border border-namm-cyan/30 px-1">
                                {s.roomName || 'Salle'}
                              </span>
                            </div>
                            <div className="text-[10px] text-namm-muted flex items-center gap-2 mt-0.5">
                              <span>
                                {startTime} - {endTime}
                              </span>
                              <span>·</span>
                              <span className="text-green-400 font-semibold font-mono">
                                {formatDuration(s.totalDurationMs || 0)}
                              </span>
                              <span>·</span>
                              <span>par {s.stoppedBy || 'Anonyme'}</span>
                            </div>
                            {games.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {games.map((g, idx) => (
                                  <span key={idx} className="text-[8px] bg-namm-cyan/10 border border-namm-cyan/30 text-namm-cyan px-1 py-0.2 rounded font-mono">
                                    🎮 {g.game}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Detailed List View */
        <div className="space-y-2">
          {daySessions.map((s) => {
            const startDate = s.startedAt ? new Date(s.startedAt) : null;
            const endDate = s.endedAt ? new Date(s.endedAt) : null;
            const timeRange = `${
              startDate
                ? startDate.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-'
            } ➔ ${
              endDate
                ? endDate.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-'
            }`;

            const games: PlayedGameEntry[] = Array.isArray(s.playedGames)
              ? s.playedGames
              : s.playedGames
              ? Object.values(s.playedGames as Record<string, PlayedGameEntry>)
              : [];

            return (
              <div
                key={s.id}
                className="p-4 border border-namm-line/30 bg-namm-panel/60 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-namm-cyan/40 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 border border-namm-cyan/40 bg-namm-cyan/10 flex items-center justify-center text-namm-cyan font-bold text-xs">
                    ⏱
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-base uppercase text-namm-text">
                        "{s.timerName || 'Sans titre'}"
                      </h3>
                      <span className="text-xs font-mono text-namm-cyan border border-namm-cyan/30 px-1.5 py-0.5">
                        {s.roomName || 'Salle'}
                      </span>
                      <span className="text-[10px] uppercase text-namm-muted border border-namm-line/30 px-1 py-0.5">
                        {s.type || '-'}
                      </span>
                    </div>
                    <div className="text-xs text-namm-muted mt-1">
                      {timeRange} <span className="mx-2">·</span> Enregistré par{' '}
                      <span className="text-namm-text font-semibold">{s.stoppedBy || 'Anonyme'}</span>
                    </div>
                    {/* Game breakdown */}
                    {games.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {games.map((g, idx) => (
                          <span
                            key={idx}
                            className="text-[9px] font-mono bg-namm-cyan/10 border border-namm-cyan/30 text-namm-cyan px-1.5 py-0.5"
                          >
                            🎮 {g.game}
                            {g.durationMs && (
                              <span className="text-namm-muted ml-1 font-normal">
                                ({formatDuration(g.durationMs)})
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <span className="text-xl font-heading font-bold text-green-400 tabular-nums block">
                    {formatDuration(s.totalDurationMs || 0)}
                  </span>
                  <span className="text-[10px] uppercase text-namm-muted tracking-widest block">
                    Durée totale
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
